import { Detailkarte } from './Detailkarte';
import { KORB, ZUSTANDSFARBE, farbzustand, z } from './gemeinsam';
import type { ApiClient } from '../../api';
import {
  ContentSection, DataTable, EmptyState, Feld, KpiCard, KpiReihe,
  LoadingState, StatusBadge,
} from '../../muster';

interface Filter {
  befund: string; gewerk: string; geschoss: string;
  bereich: string; richtung: string; seite: number;
}

/**
 * Abarbeitung: der Arbeitsvorrat (acht Körbe nach Handlung), die Vorschläge
 * — wer weicht aus (Stufe 2, Höhenbänder, Nachrechnung) und die
 * Abstandsmängel.
 *
 * --- Warum die Körbe nach Handlung geordnet sind, nicht nach Tiefe ---------
 *
 * Diese Karte ist der Grund, warum die Seite jetzt etwas nützt. Vorher stand
 * hier eine Liste mit 318.000 Zeilen, alle mit derselben Einstufung „echt" —
 * und niemand konnte ihr entnehmen, was zu tun ist.
 */
export function Arbeitsvorrat({
  vorrat, korb, korbseite, onKorbLaden, onKorbBlaettern,
  detail, detailLaedt, onDetailLaden,
  stufe2, rechnet, onVorschlagen,
  vorschlaege, vf, onVfChange,
  abstaende,
  api, projektId,
}: {
  api: ApiClient;
  projektId: string;
  vorrat: any;
  korb: string | null;
  korbseite: number;
  onKorbLaden: (korb: string) => void;
  onKorbBlaettern: (seite: number) => void;
  detail: any;
  detailLaedt: string | null;
  onDetailLaden: (kollisionId: string) => void;
  stufe2: any;
  rechnet: boolean;
  onVorschlagen: () => void;
  vorschlaege: any;
  vf: Filter;
  onVfChange: (updater: (f: Filter) => Filter) => void;
  abstaende: any;
}) {
  return (
    <>
      {/* ══════════════════════════════════════════════════════════════════
          DER ARBEITSVORRAT — GANZ OBEN

          Die Körbe sind nach der nächsten HANDLUNG geordnet, nicht nach
          Durchdringungstiefe. Die Tiefe war die erste Ordnung und die
          falsche: Sie stellt Leerrohre im Wandaufbau nach oben und sagt
          nichts darüber, ob jemand etwas tun kann.
          ══════════════════════════════════════════════════════════════════ */}
      {vorrat && (vorrat.koerbe ?? []).length > 0 && (
        <ContentSection
          titel={`Arbeitsvorrat — ${z(vorrat.gesamt ?? 0)} offene Durchdringungen`}
          beschreibung={'Die Körbe sind nach der nächsten HANDLUNG geordnet, nicht nach '
            + 'Durchdringungstiefe. Die Tiefe war die erste Ordnung und die falsche: '
            + 'Sie stellt Leerrohre im Wandaufbau nach oben und sagt nichts darüber, ob '
            + 'jemand etwas tun kann.'}
          nackt
        >
          {vorrat.hinweis && (
            <div className={
              (vorrat.koerbe ?? []).some((x: any) => x.korb === 'sofort')
                ? 'hinweis gut' : 'hinweis'
            }>
              {vorrat.hinweis}
            </div>
          )}

          <DataTable
            name="Körbe"
            spalten={[
              {
                schluessel: 'korb', titel: 'Korb', art: 'status', breite: '210px',
                immerSichtbar: true,
                wert: (b2: any) => (KORB[b2.korb]?.text ?? b2.korb),
                inhalt: (b2: any) => {
                  const m = KORB[b2.korb] ?? { farbe: ZUSTANDSFARBE.neutral, text: b2.korb };
                  return <StatusBadge zustand={farbzustand(m.farbe)} text={m.text} klein />;
                },
              },
              {
                schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', breite: '110px',
                wert: (b2: any) => b2.anzahl,
              },
              {
                schluessel: 'tiefste', titel: 'tiefste', art: 'zahl', einheit: 'mm',
                nachkomma: 0, breite: '120px',
                wert: (b2: any) => b2.tiefsteMm,
              },
              {
                schluessel: 'handlung', titel: 'was zu tun ist',
                wert: (b2: any) => b2.handlung,
                inhalt: (b2: any) => (
                  <span style={{ fontSize: 12 }}>{b2.handlung}</span>
                ),
              },
            ]}
            zeilen={vorrat.koerbe as any[]}
            schluessel={(b2: any) => b2.korb}
            beiZeileKlick={(b2: any) => onKorbLaden(b2.korb)}
            aktiveZeile={korb ?? undefined}
            suche={false}
            sortierung={{ spalte: 'anzahl', richtung: 'ab' }}
            ausfuhr
            ausfuhrDateiname="Arbeitsvorrat-Koerbe"
          />

          {/* --- Die Gewerkepaare, NORMALISIERT ------------------------
              Vorher standen „elt × rlt" und „rlt × elt" als zwei Zeilen da.
              Es ist dasselbe Paar — welches Gewerk links landet, entscheidet
              allein, welche Bauteil-Guid kleiner war. Wer nach einem Paar
              filterte, sah die Hälfte. */}
          {(vorrat.gewerkepaare ?? []).length > 0 && (
            <DataTable
              name="Gewerkepaare — und wie viel davon sofort geht"
              spalten={[
                {
                  schluessel: 'paar', titel: 'Paar', immerSichtbar: true,
                  wert: (x: any) => x.paar,
                },
                {
                  schluessel: 'gesamt', titel: 'gesamt', art: 'zahl',
                  wert: (x: any) => x.anzahl,
                },
                {
                  schluessel: 'sofort', titel: 'sofort', art: 'zahl', breite: '120px',
                  wert: (x: any) => x.sofort,
                  inhalt: (x: any) => (x.sofort > 0
                    ? <StatusBadge zustand="gut" text={z(x.sofort)} klein
                                   titel="sofort umsetzbar" />
                    : '0'),
                },
              ]}
              zeilen={vorrat.gewerkepaare as any[]}
              schluessel={(x: any) => x.paar}
              hoehe={240}
              sortierung={{ spalte: 'gesamt', richtung: 'ab' }}
              ausfuhr
              ausfuhrDateiname="Gewerkepaare"
              dicht
            />
          )}

          {/* --- Die Einzelfälle des gewählten Korbes ------------------- */}
          {korb && (
            <>
              <DataTable
                name={`${KORB[korb]?.text ?? korb} — Einzelfälle`}
                spalten={[
                  {
                    schluessel: 'geschoss', titel: 'Geschoss', breite: '120px',
                    immerSichtbar: true,
                    wert: (f: any) => f.geschoss ?? '',
                    inhalt: (f: any) => f.geschoss ?? '—',
                  },
                  {
                    schluessel: 'abschnitt', titel: 'Abschnitt', breite: '140px',
                    wert: (f: any) => f.planungsbereich ?? '',
                    inhalt: (f: any) => (
                      <span style={{ fontSize: 11 }}>{f.planungsbereich ?? '—'}</span>
                    ),
                  },
                  { schluessel: 'paar', titel: 'Paar', wert: (f: any) => f.paar },
                  {
                    schluessel: 'tief', titel: 'tief', art: 'zahl', einheit: 'mm',
                    nachkomma: 0, breite: '110px',
                    wert: (f: any) => f.durchdringungMm,
                  },
                  {
                    schluessel: 'vorschlag', titel: 'Vorschlag',
                    wert: (f: any) => (f.betrag
                      ? `${f.richtung ?? ''} ${Math.round(f.betrag)} mm`
                      : (f.befund ?? '')),
                    inhalt: (f: any) => (
                      <span style={{ fontSize: 11 }}>
                        {f.betrag
                          ? `${f.richtung ?? ''} ${Math.round(f.betrag)} mm`
                          : (f.befund ?? '—')}
                        {f.mitzieher > 0 && (
                          <> <StatusBadge
                            klein zustand="warnung"
                            text={`+${f.mitzieher} mitziehen`}
                          /></>
                        )}
                      </span>
                    ),
                  },
                  {
                    schluessel: 'ausweicht', titel: 'weicht aus', breite: '130px',
                    wert: (f: any) => f.ausweicht ?? '',
                    inhalt: (f: any) => (
                      <span style={{ fontSize: 11 }}>{f.ausweicht ?? '—'}</span>
                    ),
                  },
                ]}
                zeilen={(vorrat.faelle ?? []) as any[]}
                schluessel={(f: any) => f.id}
                beiZeileKlick={(f: any) => onDetailLaden(f.id)}
                aktiveZeile={detail?.kollision?.id ?? undefined}
                // Geblättert wird in der DATENBANK, 50 je Seite — die Tabelle
                // zählt deshalb nicht selbst.
                proSeite={0}
                trefferText={`${z(vorrat.gesamtImKorb ?? 0)} Fälle · Seite ${korbseite + 1}`}
                suche={false}
                hoehe={420}
                dicht
                werkzeuge={(
                  <>
                    <button className="m-knopf" disabled={korbseite === 0}
                            onClick={() => onKorbBlaettern(korbseite - 1)}>
                      ← zurück
                    </button>
                    <button className="m-knopf"
                            disabled={(korbseite + 1) * 50 >= (vorrat.gesamtImKorb ?? 0)}
                            onClick={() => onKorbBlaettern(korbseite + 1)}>
                      weiter →
                    </button>
                  </>
                )}
                leer={(
                  <EmptyState
                    titel="Kein Fall in diesem Korb."
                    erklaerung={'Entweder ist er abgearbeitet, oder die Einstufung hat '
                      + 'ihn einem anderen Korb zugeordnet.'}
                  />
                )}
              />

              {detail && <Detailkarte d={detail} api={api} projektId={projektId} />}
            </>
          )}
        </ContentSection>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          STUFE 2 — Vorschlaege nach Gewerkevorrang
          ══════════════════════════════════════════════════════════════════ */}
      <ContentSection
        titel="Vorschläge — wer weicht aus"
        beschreibung={'Zuerst die HÖHENBÄNDER: „Die Heizungsleitung 120 mm tiefer '
          + 'legen" klingt nach einer Lösung und ist keine, wenn 80 mm darunter die '
          + 'Abhangdecke sitzt. Rohdecke von unten aus dem Tragwerk, Abhangdecke von '
          + 'oben aus der Architektur, Unterzüge als schärfere obere Grenze. Dann der '
          + 'Vorrang — SEITLICH VOR HÖHE: Seitlich ausweichen kostet keine '
          + 'zusätzlichen Bögen und rührt nicht an das Band, das die anderen Gewerke '
          + 'auch noch brauchen.'}
        aktionen={(
          <button className="m-knopf" onClick={onVorschlagen} disabled={rechnet}>
            {rechnet ? 'rechnet …' : 'Vorschläge rechnen'}
          </button>
        )}
      >
        {!stufe2 && (
          <EmptyState
            titel="Noch keine Vorschläge gerechnet."
            erklaerung={'Die Rechnung braucht die Höhenbänder und den Gewerkevorrang — '
              + 'beide stehen unter „Grundlagen".'}
            aktion={(
              <button className="m-knopf m-knopf-haupt" onClick={onVorschlagen}
                      disabled={rechnet}>
                {rechnet ? 'rechnet …' : 'Vorschläge rechnen'}
              </button>
            )}
          />
        )}

        {stufe2 && (
          <>
            <KpiReihe>
              <KpiCard titel="Machbar" wert={stufe2.machbar}
                       zustand={stufe2.machbar > 0 ? 'gut' : undefined}
                       zustandText={stufe2.machbar > 0 ? 'Vorschlag trägt' : undefined} />
              <KpiCard titel="Eng" wert={stufe2.eng}
                       zustand={stufe2.eng > 0 ? 'warnung' : undefined}
                       zustandText={stufe2.eng > 0 ? 'Band fast aufgebraucht' : undefined} />
              <KpiCard
                titel="Ohne Lösung" wert={stufe2.keineLoesung}
                vergleich="Planungsentscheidung"
                zustand={stufe2.keineLoesung > 0 ? 'fehler' : undefined}
                zustandText={stufe2.keineLoesung > 0 ? 'kein Gewerk kann ausweichen' : undefined}
              />
              <KpiCard titel="Gleicher Rang" wert={stufe2.nichtGeprueft}
                       vergleich="Frage an die Planer" />
              <KpiCard titel="Mittlere Bandhöhe"
                       wert={stufe2.baender?.mittlereHoeheMm ?? 0}
                       einheit="mm" nachkomma={0} />
            </KpiReihe>

            {(stufe2.baender?.hinweise ?? []).map((h: string, i: number) => (
              <div key={`b${i}`} className="hinweis">{h}</div>
            ))}

            {(stufe2.hinweise ?? []).map((h: string, i: number) => (
              <div key={i} className="hinweis">{h}</div>
            ))}

            {/* ══════════════════════════════════════════════════════════
                DIE NACHRECHNUNG
                ══════════════════════════════════════════════════════════

                'Ein Vorschlag ohne diese Prüfung ist eine geometrische
                Übung.' Sie läuft über die UniqueId gegen die Rohre und
                Kanäle — dort steht der gerechnete Druckverlust je ELEMENT,
                und daraus folgt das Druckgefälle in Pa je Meter. */}
            {stufe2.nachrechnung && (
              <div style={{ marginTop: 14, paddingTop: 12,
                            borderTop: '1px solid var(--linie)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Nachrechnung — was die Vorschläge hydraulisch auslösen
                </div>

                <KpiReihe>
                  <KpiCard titel="Nachgerechnet"
                           wert={stufe2.nachrechnung.gerechnet} />
                  <KpiCard
                    titel="Nicht rechenbar" wert={stufe2.nachrechnung.nichtRechenbar}
                    zustand={stufe2.nachrechnung.nichtRechenbar > 0 ? 'warnung' : undefined}
                    zustandText={stufe2.nachrechnung.nichtRechenbar > 0
                      ? 'ohne Druckverlust am Element' : undefined}
                  />
                  <KpiCard
                    titel="Zusätzlich" wert={stufe2.nachrechnung.summeZusatzPa}
                    einheit="Pa" vergleich="obere Schranke"
                  />
                </KpiReihe>

                {[...(stufe2.nachrechnung.hinweise ?? []),
                  ...(stufe2.nachrechnung.gegenproben ?? [])]
                  .map((h: string, i: number) => (
                    <div key={i} className="schwach"
                         style={{ fontSize: 12, marginBottom: 5 }}>{h}</div>
                  ))}
              </div>
            )}

            {(stufe2.gegenproben ?? []).length > 0 && (
              <div style={{ marginTop: 14, paddingTop: 12,
                            borderTop: '1px solid var(--linie)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Gegenproben
                </div>

                {(stufe2.gegenproben ?? []).map((g: string, i: number) => (
                  <div key={i} className="schwach"
                       style={{ fontSize: 12, marginBottom: 5 }}>{g}</div>
                ))}
              </div>
            )}
          </>
        )}
      </ContentSection>

      {/* --- Die Vorschlaege selbst -------------------------------------- */}
      {vorschlaege && (vorschlaege.gesamt ?? 0) > 0 && (
        <ContentSection
          titel="Die Vorschläge — ohne Lösung zuerst"
          meta={(vorschlaege.jeBefund ?? []).length > 0
            ? (vorschlaege.jeBefund ?? [])
              .map((b2: any) => `${b2.befund}: ${b2.anzahl.toLocaleString('de-DE')}`)
              .join(' · ')
            : undefined}
          nackt
        >

          {/* ══════════════════════════════════════════════════════════════
              DIE FILTERLEISTE

              Vier Fragen, die der Planer wirklich stellt: In welchem
              Geschoss, in welchem Abschnitt, wessen Gewerk weicht, und um
              welche Art Lösung geht es. Dazu der Befund, weil "ohne Lösung"
              die einzige Gruppe ist, die eine Entscheidung braucht.

              Jede Auswahl zeigt nur Werte, die es in den Daten GIBT.
              ═════════════════════════════════════════════════════════ */}
          <div className="m-feldgitter m-feldgitter-3">
            {([
              ['geschoss', 'Geschoss', vorschlaege.filterwerte?.geschosse],
              ['bereich', 'Abschnitt', vorschlaege.filterwerte?.bereiche],
              ['gewerk', 'Gewerk (weicht)', vorschlaege.filterwerte?.gewerke],
              ['richtung', 'Lösungsart', vorschlaege.filterwerte?.richtungen],
              ['befund', 'Befund', vorschlaege.filterwerte?.befunde],
            ] as const).map(([feld, titel, werte]) => (
              <Feld
                key={feld}
                art="auswahl"
                beschriftung={titel}
                wert={(vf as any)[feld]}
                beiAenderung={(w) => onVfChange((f) => ({
                  ...f, [feld]: w as string, seite: 1 }))}
                optionen={[
                  { wert: '', beschriftung: 'alle' },
                  ...((werte ?? []) as string[]).map((w: string) => ({
                    wert: w, beschriftung: w,
                  })),
                ]}
              />
            ))}
          </div>

          {(vf.geschoss || vf.bereich || vf.gewerk || vf.richtung || vf.befund) && (
            <button
              className="m-knopf"
              onClick={() => onVfChange(() => ({ befund: '', gewerk: '',
                geschoss: '', bereich: '', richtung: '', seite: 1 }))}
            >
              Filter zurücksetzen
            </button>
          )}

          <p className="schwach" style={{ fontSize: 12 }}>
            Wo keine Lösung ist, steht kein Betrag, sondern die
            <strong> fehlende Höhe</strong>. Eine Kollision im vollen
            Deckenhohlraum braucht eine Planungsentscheidung — Abhangdecke
            tiefer, Unterzug durchbrechen, Trasse umlegen.
          </p>

          <DataTable
            spalten={[
              {
                schluessel: 'geschoss', titel: 'Geschoss', breite: '120px',
                immerSichtbar: true,
                wert: (v: any) => v.geschoss ?? '',
                inhalt: (v: any) => v.geschoss ?? '—',
              },
              {
                schluessel: 'abschnitt', titel: 'Abschnitt', breite: '140px',
                wert: (v: any) => v.planungsbereich ?? '',
                inhalt: (v: any) => v.planungsbereich ?? '—',
              },
              {
                schluessel: 'weicht', titel: 'weicht', breite: '110px',
                wert: (v: any) => v.gewerk ?? '',
                inhalt: (v: any) => v.gewerk ?? '—',
              },
              {
                schluessel: 'gegen', titel: 'gegen', breite: '110px',
                wert: (v: any) => v.gegenGewerk ?? '',
                inhalt: (v: any) => v.gegenGewerk ?? '—',
              },
              {
                schluessel: 'richtung', titel: 'Richtung', breite: '130px',
                wert: (v: any) => v.richtung,
              },
              {
                /* Wo keine Lösung ist, steht kein Betrag, sondern die
                   FEHLENDE HÖHE. Eine Kollision im vollen Deckenhohlraum
                   braucht eine Planungsentscheidung — ein Werkzeug, das dort
                   etwas erfindet, verliert das Vertrauen; und dann werden auch
                   die guten Vorschläge nicht mehr gelesen. */
                schluessel: 'betrag', titel: 'Betrag', ausrichtung: 'rechts',
                einheit: 'mm', breite: '160px',
                wert: (v: any) => v.betragMm ?? v.fehlendeHoeheMm ?? null,
                inhalt: (v: any) => {
                  if (v.betragMm) return `${Math.round(v.betragMm)}`;
                  if (v.fehlendeHoeheMm) {
                    return (
                      <StatusBadge
                        klein zustand="fehler"
                        text={`${Math.round(v.fehlendeHoeheMm)} mm fehlen`}
                      />
                    );
                  }
                  return '—';
                },
              },
              {
                schluessel: 'mehrlaenge', titel: 'Mehrlänge', art: 'zahl',
                einheit: 'm', nachkomma: 2, breite: '130px',
                wert: (v: any) => v.mehrlaengeM,
              },
              {
                schluessel: 'boegen', titel: 'Bögen', art: 'zahl', breite: '100px',
                wert: (v: any) => v.zusatzBoegen,
              },
              {
                schluessel: 'nachweis', titel: 'Nachweis', art: 'status',
                breite: '170px',
                wert: (v: any) => (v.nachweisGeprueft
                  ? (v.nachweisHaelt ? 'hält' : 'hält nicht') : 'nicht gerechnet'),
                inhalt: (v: any) => {
                  if (!v.nachweisGeprueft) {
                    return <StatusBadge zustand="neutral" text="nicht gerechnet" klein />;
                  }
                  return v.nachweisHaelt
                    ? <StatusBadge zustand="gut" text="hält" klein />
                    : <StatusBadge zustand="fehler" text="hält NICHT" klein />;
                },
              },
              {
                schluessel: 'befund', titel: 'Befund', art: 'status', breite: '170px',
                wert: (v: any) => v.befund,
                inhalt: (v: any) => (
                  <StatusBadge
                    klein
                    zustand={v.befund === 'keine_loesung' ? 'fehler'
                      : v.befund === 'eng' ? 'warnung' : 'neutral'}
                    text={v.befund}
                  />
                ),
              },
            ]}
            zeilen={(vorschlaege.vorschlaege ?? []) as any[]}
            schluessel={(v: any) => v.id}
            beiZeileKlick={(v: any) => onDetailLaden(v.kollisionId)}
            aktiveZeile={detail?.kollision?.id ?? undefined}
            // Geblättert wird auf dem SERVER; die Trefferzahl kommt aus der
            // Antwort. Ohne sie sähen 100 von 40.000 aus wie 100 von 100.
            proSeite={0}
            trefferText={`${(vorschlaege.gesamt ?? 0).toLocaleString('de-DE')} Vorschläge`
              + ((vorschlaege.seiten ?? 1) > 1
                ? ` · Seite ${vorschlaege.seite} von ${vorschlaege.seiten}` : '')}
            suche={false}
            hoehe={520}
            dicht
            leer={(
              <EmptyState
                titel="Kein Vorschlag in dieser Auswahl."
                erklaerung="Die Filter oben sind zu eng gesetzt."
              />
            )}
          />

          {detailLaedt && <LoadingState text="Detail wird geholt …" zeilen={4} />}

          {detail && <Detailkarte d={detail} api={api} projektId={projektId} />}

          {(vorschlaege.seiten ?? 1) > 1 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                          marginTop: 10 }}>
              <button
                className="m-knopf"
                disabled={(vorschlaege.seite ?? 1) <= 1}
                onClick={() => onVfChange((f) => ({ ...f, seite: f.seite - 1 }))}
              >
                ← zurück
              </button>

              <span className="schwach" style={{ fontSize: 12 }}>
                Seite {vorschlaege.seite} von {vorschlaege.seiten}
              </span>

              <button
                className="m-knopf"
                disabled={(vorschlaege.seite ?? 1) >= (vorschlaege.seiten ?? 1)}
                onClick={() => onVfChange((f) => ({ ...f, seite: f.seite + 1 }))}
              >
                weiter →
              </button>
            </div>
          )}
        </ContentSection>
      )}

      {/* --- Die Hoehenbaender ------------------------------------------- */}
      {vorschlaege && (vorschlaege.hoehenbaender ?? []).length > 0 && (
        <ContentSection
          titel="Die Höhenbänder — was überhaupt zur Verfügung steht"
          nackt
        >

          <DataTable
            spalten={[
              {
                schluessel: 'geschoss', titel: 'Geschoss', immerSichtbar: true,
                breite: '120px', wert: (h: any) => h.geschoss,
              },
              {
                schluessel: 'abschnitt', titel: 'Abschnitt', breite: '140px',
                wert: (h: any) => h.planungsbereich ?? '',
                inhalt: (h: any) => h.planungsbereich ?? '—',
              },
              {
                schluessel: 'rohdecke', titel: 'Rohdecke UK', art: 'zahl',
                einheit: 'mm', nachkomma: 0, wert: (h: any) => h.rohdeckeUkMm,
              },
              {
                schluessel: 'unterzug', titel: 'Unterzug UK', art: 'zahl',
                einheit: 'mm', nachkomma: 0, wert: (h: any) => h.unterzugUkMm,
              },
              {
                schluessel: 'abhang', titel: 'Abhang OK', art: 'zahl',
                einheit: 'mm', nachkomma: 0, wert: (h: any) => h.abhangOkMm,
              },
              {
                schluessel: 'band', titel: 'Band', art: 'status', breite: '140px',
                wert: (h: any) => h.bandHoeheMm,
                inhalt: (h: any) => (h.bandHoeheMm != null
                  ? <strong>{Math.round(h.bandHoeheMm)} mm</strong>
                  : <StatusBadge zustand="fehler" text="keines" klein />),
              },
              {
                schluessel: 'befund', titel: 'Befund',
                wert: (h: any) => h.befund ?? '',
                inhalt: (h: any) => (
                  <span className="schwach" style={{ fontSize: 11 }}>
                    {h.befund ?? ''}
                  </span>
                ),
              },
            ]}
            zeilen={(vorschlaege.hoehenbaender ?? []) as any[]}
            schluessel={(h: any) => `${h.geschoss}|${h.planungsbereich}`}
            hoehe={420}
            ausfuhr
            ausfuhrDateiname="Hoehenbaender"
            dicht
          />
        </ContentSection>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          DIE ABSTANDSMAENGEL — eine eigene Frage
          ══════════════════════════════════════════════════════════════════ */}
      {abstaende && abstaende.gesamt > 0 && (
        <ContentSection
          titel={`Abstandsmängel — ${z(abstaende.gesamt)} zu enge Paare`}
          beschreibung={'Diese Bauteile stecken NICHT ineinander. Sie liegen näher '
            + 'beieinander als Dämmung plus Montagezuschlag — ein anderer Mangel mit '
            + 'einer anderen Lösung, und manchmal eine Entscheidung: Hier wird eben '
            + 'ohne Montageabstand gebaut, und das gehört dokumentiert und nicht '
            + 'verschoben.'}
          nackt
        >
          <DataTable
            name="Abstandsmängel je Gewerkepaar"
            spalten={[
              {
                schluessel: 'paar', titel: 'Gewerkepaar', immerSichtbar: true,
                wert: (p2: any) => p2.paar,
              },
              {
                schluessel: 'anzahl', titel: 'Paare', art: 'zahl',
                wert: (p2: any) => p2.anzahl,
              },
              {
                schluessel: 'groesste', titel: 'größte Unterschreitung', art: 'zahl',
                einheit: 'mm', nachkomma: 0, breite: '220px',
                wert: (p2: any) => p2.groessteMm,
                inhalt: (p2: any) => (
                  <StatusBadge zustand="warnung" text={`${z(p2.groessteMm)} mm`} klein />
                ),
              },
              {
                schluessel: 'mittel', titel: 'im Mittel', art: 'zahl',
                einheit: 'mm', nachkomma: 0,
                wert: (p2: any) => p2.mittelMm,
              },
            ]}
            // Der `.slice(0, 15)` des Bestands ist entfallen: `DataTable`
            // virtualisiert ab 200 Zeilen und zeigt deshalb alle.
            zeilen={(abstaende.jeGewerkepaar ?? []) as any[]}
            schluessel={(p2: any) => p2.paar}
            sortierung={{ spalte: 'anzahl', richtung: 'ab' }}
            ausfuhr
            ausfuhrDateiname="Abstandsmaengel"
          />

          {(abstaende.hinweise ?? []).map((h: string, i: number) => (
            <div key={i} className="hinweis">{h}</div>
          ))}
        </ContentSection>
      )}
    </>
  );
}
